import { EMPTY_LIST, ENTITY_LISTING_VIEW_TYPE } from '../../../constants/common'
import {
  RENTAL_PORTAL_VIEWING_STATUS,
  RENTAL_PORTAL_VIEWING_STATUS_SLOT_OPEN,
} from '../../../constants/rentalPortal'
import {
  getListViewTypeWithoutPreview,
  getListViewTypeWithPreview,
  isListViewTypeCalendar,
  isListViewTypeWithPreview,
} from '../../../utils/listViewType'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../../utils/massActions'
import { TextListItem } from '../../ListItem'
import { VIEWINGS_FILTER_PARAM } from '../../RentalPortalViewing'

import {
  VIEWING_ACTION_ARCHIVE,
  VIEWING_ACTION_ARCHIVE_MASS,
  VIEWING_ACTION_BOOK,
  VIEWING_ACTION_CANCEL,
  VIEWING_ACTION_OPEN,
  VIEWING_ACTION_PERMISSION_NAME,
  VIEWING_ACTION_RESCHEDULE,
  VIEWING_VIEW_REGIME,
  VIEWING_VIEW_REGIME_LABEL_KEY,
} from './RentalPortalViewings.constants'

import PreviewIcon from '../../../icons/preview.svg'
import RentalPortalIcon from '../../../icons/rental-portal.svg'
import TableViewIcon from '../../../icons/table-view.svg'
import CalendarViewIcon from '../../../icons/viewing.svg'

function toTranslatedViewingsFilterOption(
  { label_key: labelKey, ...option },
  t
) {
  return labelKey ? { ...option, label: t(labelKey) } : option
}

function getIsViewingActionPermitted({ action, item }) {
  return Boolean(item.permissions?.[VIEWING_ACTION_PERMISSION_NAME[action]])
}

function getViewingsListingListItemProps() {
  return { ListIcon: RentalPortalIcon }
}

export function getIsViewingConfirmed(item) {
  return item.status === RENTAL_PORTAL_VIEWING_STATUS.confirmed
}

export function getViewingTableItemActions(item) {
  const items = [
    {
      label: 'RentalPortal.ListingProfileApplicantsOpenActionOptionKey',
      permission: true,
      value: VIEWING_ACTION_OPEN,
    },
    {
      label: 'RentalPortal.ViewingFormBookSubmitKey',
      permission:
        item.status === RENTAL_PORTAL_VIEWING_STATUS_SLOT_OPEN &&
        getIsViewingActionPermitted({ action: VIEWING_ACTION_BOOK, item }),
      value: VIEWING_ACTION_BOOK,
    },
    {
      label: 'RentalPortal.ViewingRescheduleActionKey',
      permission:
        getIsViewingConfirmed(item) &&
        getIsViewingActionPermitted({
          action: VIEWING_ACTION_RESCHEDULE,
          item,
        }),
      value: VIEWING_ACTION_RESCHEDULE,
    },
    {
      label: 'RentalPortal.ViewingCancelActionKey',
      permission:
        getIsViewingConfirmed(item) &&
        getIsViewingActionPermitted({ action: VIEWING_ACTION_CANCEL, item }),
      value: VIEWING_ACTION_CANCEL,
    },
    null,
    {
      label: 'RentalPortal.ViewingArchiveActionOptionKey',
      permission: getIsViewingActionPermitted({
        action: VIEWING_ACTION_ARCHIVE,
        item,
      }),
      value: VIEWING_ACTION_ARCHIVE,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getViewingViewRegimeOptions({
  isSplitAvailable,
  listViewType,
}) {
  const isCalendarBody = isListViewTypeCalendar(listViewType)

  const hasPreview = isListViewTypeWithPreview(listViewType)

  return [
    {
      Icon: TableViewIcon,
      isActive: !isCalendarBody,
      label: VIEWING_VIEW_REGIME_LABEL_KEY[VIEWING_VIEW_REGIME.table],
      name: VIEWING_VIEW_REGIME.table,
      value: hasPreview
        ? ENTITY_LISTING_VIEW_TYPE.tableWithPreview
        : ENTITY_LISTING_VIEW_TYPE.table,
    },
    {
      Icon: CalendarViewIcon,
      isActive: isCalendarBody,
      label: VIEWING_VIEW_REGIME_LABEL_KEY[VIEWING_VIEW_REGIME.calendar],
      name: VIEWING_VIEW_REGIME.calendar,
      value: hasPreview
        ? ENTITY_LISTING_VIEW_TYPE.calendarWithPreview
        : ENTITY_LISTING_VIEW_TYPE.calendar,
    },
    ...(isSplitAvailable
      ? [
          {
            Icon: PreviewIcon,
            isActive: hasPreview,
            label: VIEWING_VIEW_REGIME_LABEL_KEY[VIEWING_VIEW_REGIME.split],
            name: VIEWING_VIEW_REGIME.split,
            value: hasPreview
              ? getListViewTypeWithoutPreview(listViewType)
              : getListViewTypeWithPreview(listViewType),
          },
        ]
      : EMPTY_LIST),
  ]
}

export function getIsViewingArchivable(viewing) {
  return Boolean(viewing?.permissions?.can_archive)
}

export function getViewingsMassActions({ eligibleCount, selectedSize, t }) {
  const hasUnusableSelection = selectedSize > 0 && eligibleCount === 0

  return [
    {
      disabled: hasUnusableSelection,
      label: t('RentalPortal.ViewingArchiveActionOptionKey'),
      permission: true,
      tooltipContent: hasUnusableSelection
        ? t('RentalPortal.ViewingArchiveMassNotAllowedTooltipKey')
        : null,
      value: VIEWING_ACTION_ARCHIVE_MASS,
    },
  ]
}

export function getIsViewingsListingFilter(filter) {
  return filter.name === VIEWINGS_FILTER_PARAM.listing
}

export function getTranslatedViewingsAvailableFilters({ availableFilters, t }) {
  return availableFilters.map(filter =>
    filter.options
      ? {
          ...filter,
          options: filter.options.map(option => ({
            ...toTranslatedViewingsFilterOption(option, t),
            ...(getIsViewingsListingFilter(filter)
              ? {
                  ListItem: TextListItem,
                  getListItemProps: getViewingsListingListItemProps,
                }
              : null),
          })),
        }
      : filter
  )
}

export function getViewingsRefreshErrorText({
  availableFilters,
  filterValues,
  t,
}) {
  const activeFilterTitles = availableFilters
    .filter(filter =>
      [filter.query_param].flat().some(param => filterValues[param])
    )
    .map(filter => t(filter.title))

  return activeFilterTitles.length > 0
    ? t('RentalPortal.ViewingsRefreshErrorFiltersKey', {
        filters: activeFilterTitles.join(', '),
      })
    : t('RentalPortal.ViewingsRefreshErrorKey')
}

export function getIsViewingActionOpen(action) {
  return action === VIEWING_ACTION_OPEN
}
